import React from 'react';
import { useTranslation } from 'react-i18next';
import './direct-availability.scss';
import { useDirectAvailability, DirectAvailabilityProvider } from './hooks/useDirectAvailability';
import Loading from '../../components/ui/Loading/Loading';
import { UnsavedChangesModal } from '../../components/EditProfileProfessional/UnsavedChangesModal/UnsavedChangesModal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { CalendarViewport } from './components/CalendarViewport/CalendarViewport';
import { EditorColumn } from './components/EditorColumn/EditorColumn';
import { AlertSnackbar } from '../../components/ui/AlertSnackbar';
import { useNavigate } from 'react-router-dom';

const DirectAvailabilityPageContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    loading,
    error,
    actions,
    showUnsavedModal,
    setShowUnsavedModal,
    deleteModalState,
    clearReminder,
    loadData,
    saveData,
    isSaving,
    hasChanges,
    lastSaved,
    isReminderActive,
    clearError
  } = useDirectAvailability();

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loading fullscreen text={t('directAvailability.loading', 'Cargando configuración...')} />;
  }

  const defaultConsequence = deleteModalState.type === 'override'
    ? t('directAvailability.deleteModal.consequenceOverride', 'Retirar solo la disponibilidad del intervalo de la fecha seleccionada, sin cambiar el patrón habitual ni las reservas confirmadas. El cambio quedará pendiente hasta pulsar Guardar.')
    : t('directAvailability.deleteModal.consequence', 'Esta franja dejará de ofrecerse para nuevas reservas. Las reservas confirmadas se conservarán en el calendario. El cambio quedará pendiente hasta pulsar Guardar.');

  const deleteModalDescription = deleteModalState.info
    ? `${deleteModalState.info}. ${defaultConsequence}`
    : defaultConsequence;

  const handleExitClick = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
    } else {
      navigate('/availability');
    }
  };

  return (
    <>
      <AlertSnackbar
        open={!!error}
        message={error || ''}
        onClose={clearError}
        severity="error"
      />
      <section className="workspace">
        <div className="calendar-layout">
          <CalendarViewport />
          <EditorColumn />
        </div>
        <div 
          id="editorActionZone" 
          className="save-bar-wrap editor-action-zone bw-editor-save" 
          aria-label={t('directAvailability.saveBar.ariaLabel', 'Acciones de guardado de disponibilidad')}
        >
          <div className="save-pill">
            <div className="save-state">
              <div 
                className={`save-dot ${hasChanges ? 'save-dot--dirty' : ''} ${lastSaved && !hasChanges ? 'save-dot--saved' : ''}`} 
                style={{ opacity: (!hasChanges && !lastSaved) ? 0 : 1 }} 
              />
              <div>
                <div className="save-copy">
                  {lastSaved && !hasChanges
                    ? t('directAvailability.saveBar.saved', 'Cambios guardados')
                    : isReminderActive
                      ? t('directAvailability.saveBar.reminderTitle', 'No olvides guardar los cambios.')
                      : hasChanges
                        ? t('directAvailability.saveBar.unsavedChanges', 'Tenés cambios sin guardar')
                        : t('directAvailability.saveBar.editing', 'Editando')
                  }
                </div>
                <div className="save-sub">
                  {lastSaved && !hasChanges
                    ? t('directAvailability.saveBar.savedSubtitle', 'Tu disponibilidad está actualizada.')
                    : isReminderActive
                      ? t('directAvailability.saveBar.reminderSubtitle', 'Guardá antes de salir para no perder las modificaciones realizadas.')
                      : hasChanges
                        ? t('directAvailability.saveBar.unsavedSubtitle', 'Guardalos para actualizar la información visible en tu disponibilidad.')
                        : t('directAvailability.saveBar.subtitle', 'Modificá los campos que necesites y luego guardá los cambios.')
                  }
                </div>
              </div>
            </div>
            <div className="save-actions">
              <button 
                type="button" 
                className="btn btn-save-exit" 
                id="exitBtn"
                onClick={handleExitClick}
              >
                {t('directAvailability.saveBar.exit', 'Salir')}
              </button>
              <button 
                type="button" 
                className="btn save-cta" 
                id="save"
                disabled={isSaving || (lastSaved && !hasChanges)}
                onClick={saveData}
              >
                {isSaving ? (
                  <span className="da-save-bar__spinner" />
                ) : (
                  t('directAvailability.saveBar.save', 'Guardar')
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      <ConfirmationModal
        open={deleteModalState.open}
        onClose={actions.closeDeleteModal}
        onConfirm={actions.confirmDeleteModal}
        title={t('directAvailability.deleteModal.title', 'Eliminar franja')}
        description={deleteModalDescription}
        confirmText={t('directAvailability.deleteModal.confirm', 'Eliminar franja')}
        cancelText={t('directAvailability.deleteModal.cancel', 'Cancelar')}
        confirmColor="error"
      />

      <UnsavedChangesModal 
        open={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onExitWithoutSaving={() => {
          clearReminder();
          navigate('/availability');
        }}
        onSaveAndExit={async () => {
          await saveData();
          clearReminder();
          navigate('/availability');
        }}
      />
    </>
  );
};

export const DirectAvailabilityPage: React.FC = () => {
  return (
    <DirectAvailabilityProvider>
      <DirectAvailabilityPageContent />
    </DirectAvailabilityProvider>
  );
};

export default DirectAvailabilityPage;
